import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { FormSelect } from '../../ui/Select'
import AddVariablePlaceholder from './AddVariablePlaceholder'
import FileVariableInput from './FileVariableInput'
import TextVariableInput from './TextVariableInput'
import VariableItem from './VariableItem'

const FILE_VARIABLE_TYPES = ['pdf', 'image']

const VariablesSection = ({
  getVariableErrors,
  onAddVariable,
  onRemoveVariable,
  onUpdateVariable,
  selectedVariables,
  variables
}) => {
  const availableOptions = useMemo(() => {
    const selectedIds = selectedVariables.map(item => item.variableId)
    return variables
      .filter(variable => !selectedIds.includes(variable.id))
      .map(variable => ({ value: variable.id, label: variable.name }))
  }, [variables, selectedVariables])

  const handleAddVariable = useCallback(selected => {
    if (!selected)
      return
    const variable = variables.find(item => item.id === selected.value)
    if (variable)
      onAddVariable(variable)
  }, [variables, onAddVariable])

  const handleRemove = useCallback(variableId => () => {
    onRemoveVariable(variableId)
  }, [onRemoveVariable])

  const handleValueChange = useCallback(variableId => event => {
    onUpdateVariable(variableId, 'value', event.target.value)
  }, [onUpdateVariable])

  const handleFileSelect = useCallback(variableId => file => {
    onUpdateVariable(variableId, 'file', file)
    onUpdateVariable(variableId, 'fileName', file.name)
    onUpdateVariable(variableId, 'clearFile', false)
  }, [onUpdateVariable])

  const handleFileDelete = useCallback(variableId => () => {
    onUpdateVariable(variableId, 'file', null)
    onUpdateVariable(variableId, 'fileName', '')
    onUpdateVariable(variableId, 'clearFile', false)
  }, [onUpdateVariable])

  const handleClearExistingFiles = useCallback(variableId => () => {
    onUpdateVariable(variableId, 'file', null)
    onUpdateVariable(variableId, 'fileName', '')
    onUpdateVariable(variableId, 'clearFile', true)
  }, [onUpdateVariable])

  const handleCancelClearFile = useCallback(variableId => () => {
    onUpdateVariable(variableId, 'clearFile', false)
  }, [onUpdateVariable])

  const handleClearExistingValues = useCallback(variableId => () => {
    onUpdateVariable(variableId, 'value', '')
    onUpdateVariable(variableId, 'clearValue', true)
  }, [onUpdateVariable])

  const handleCancelClearValue = useCallback(variableId => () => {
    onUpdateVariable(variableId, 'clearValue', false)
  }, [onUpdateVariable])

  const renderVariableInput = item => {
    const isFileVariable = FILE_VARIABLE_TYPES.includes(item.variableType)

    if (isFileVariable) {
      return (
        <FileVariableInput
          clearFile={item.clearFile}
          errors={getVariableErrors(item.variableId)}
          fileName={item.fileName || ''}
          variableId={item.variableId}
          variableType={item.variableType}
          onCancelClear={handleCancelClearFile(item.variableId)}
          onChange={handleFileSelect(item.variableId)}
          onClear={handleClearExistingFiles(item.variableId)}
          onDelete={handleFileDelete(item.variableId)}
        />
      )
    }

    return (
      <TextVariableInput
        clearValue={item.clearValue}
        value={item.value}
        variableId={item.variableId}
        onCancelClear={handleCancelClearValue(item.variableId)}
        onChange={handleValueChange(item.variableId)}
        onClear={handleClearExistingValues(item.variableId)}
      />
    )
  }

  const selectComponents = useMemo(() => ({ Placeholder: AddVariablePlaceholder }), [])

  return (
    <div>
      {selectedVariables.map(item => (
        <VariableItem
          key={item.variableId}
          name={item.variableName}
          onRemove={handleRemove(item.variableId)}
        >
          {renderVariableInput(item)}
        </VariableItem>
      ))}
      <FormSelect
        components={selectComponents}
        options={availableOptions}
        placeholder="Ajouter une variable à éditer"
        value={null}
        isClearable
        onChange={handleAddVariable}
      />
    </div>
  )
}

VariablesSection.propTypes = {
  getVariableErrors: PropTypes.func.isRequired,
  onAddVariable: PropTypes.func.isRequired,
  onRemoveVariable: PropTypes.func.isRequired,
  onUpdateVariable: PropTypes.func.isRequired,
  selectedVariables: PropTypes.arrayOf(PropTypes.shape({
    clearFile: PropTypes.bool,
    clearValue: PropTypes.bool,
    file: PropTypes.instanceOf(File),
    fileName: PropTypes.string,
    value: PropTypes.string.isRequired,
    variableId: PropTypes.number.isRequired,
    variableName: PropTypes.string.isRequired,
    variableType: PropTypes.string.isRequired
  })).isRequired,
  variables: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    variableType: PropTypes.string.isRequired
  })).isRequired
}

export default VariablesSection
